import { Alert, Button, DatePicker, InputNumber, Select, Space, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { FileExcelOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { usePlanDisplayIndex } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { rangeToIso } from '@/shared/lib/dayRange'
import { formatNumber } from '@/shared/lib/format'
import { downloadServerExport } from '@/shared/lib/serverExport'
import { EmptyState } from '@/shared/ui/EmptyState'
import { PageHeader } from '@/shared/ui/PageHeader'
import { QueryErrorState } from '@/shared/ui/QueryErrorState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import {
  fetchVoucherVispReport,
  voucherReportParams,
  type CardStatus,
  type VoucherReportFilter,
  type VoucherVispReportRow,
} from '@/features/voucher/voucherApi'

type CountKey = 'pending' | 'active' | 'used' | 'cancelled' | 'expired'

const COUNTS: readonly { key: CountKey; status: CardStatus }[] = [
  { key: 'pending', status: 'PENDING' },
  { key: 'active', status: 'ACTIVE' },
  { key: 'used', status: 'USED' },
  { key: 'cancelled', status: 'CANCELLED' },
  { key: 'expired', status: 'EXPIRED' },
]

function registerLink(filter: VoucherReportFilter, vispId: string, status: CardStatus): string {
  const params = new URLSearchParams({ 'f.vispId': vispId, 'f.status': status })
  for (const [name, value] of Object.entries(voucherReportParams(filter))) params.set(`f.${name}`, value)
  return `/vouchers/register?${params.toString()}`
}

/** Har VISP bo'yicha kartalar holati (ADR-056 §8) va uning Excel fayli */
export function VoucherVispReportPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const { state, patch } = useTableUrlState()
  const planNames = usePlanDisplayIndex(can('plan.read'))

  const value = (name: string) => state.filters[name]?.[0] ?? null
  const filter: VoucherReportFilter = {
    planId: value('planId'),
    batchNo: value('batchNo'),
    from: value('from'),
    to: value('to'),
  }
  const setFilter = (next: Partial<Record<keyof VoucherReportFilter, string | null>>) => {
    const filters = { ...state.filters }
    for (const [name, picked] of Object.entries(next)) filters[name] = picked ? [picked] : []
    patch({ page: 1, filters })
  }

  const report = useQuery({
    queryKey: [...queryKeys.vouchers.vispReport, filter],
    queryFn: ({ signal }) => fetchVoucherVispReport(filter, signal),
  })
  const rows = report.data ?? []

  const download = useMutation({
    mutationFn: () =>
      downloadServerExport({
        path: '/api/vouchers/report/visp/export',
        format: 'xlsx',
        params: voucherReportParams(filter),
        fallbackBaseName: 'voucher-visp',
      }),
  })

  const sum = (key: CountKey | 'total') => rows.reduce((acc, row) => acc + row[key], 0)

  const columns: ColumnsType<VoucherVispReportRow> = [
    {
      key: 'visp',
      title: t('voucher.batch.visp'),
      width: 220,
      render: (_value, row) =>
        row.vispId === null ? (
          <Typography.Text type="secondary">{t('voucher.vispReport.noVisp')}</Typography.Text>
        ) : (
          (row.vispName ?? row.vispId)
        ),
    },
    ...COUNTS.map(({ key, status }) => ({
      key,
      title: t(`voucher.register.status.${status}`),
      width: 130,
      align: 'right' as const,
      render: (_value: unknown, row: VoucherVispReportRow) =>
        row.vispId !== null && row[key] > 0 ? (
          <Link to={registerLink(filter, row.vispId, status)}>{formatNumber(row[key])}</Link>
        ) : (
          formatNumber(row[key])
        ),
    })),
    {
      key: 'total',
      title: t('voucher.vispReport.total'),
      width: 130,
      align: 'right',
      render: (_value, row) => <Typography.Text strong>{formatNumber(row.total)}</Typography.Text>,
    },
  ]

  return (
    <>
      <PageHeader
        title={t('nav.voucherVispReport')}
        description={t('pageIntro.voucherVispReport')}
        extra={
          can('report.export') ? (
            <Button
              icon={<FileExcelOutlined />}
              loading={download.isPending}
              disabled={rows.length === 0}
              onClick={() => download.mutate()}
            >
              {t('voucher.vispReport.export')}
            </Button>
          ) : null
        }
      />

      {download.isError ? (
        <Alert
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          message={translateError(download.error, t)}
          onClose={() => download.reset()}
        />
      ) : null}

      <Space wrap style={{ marginBottom: 16 }}>
        <DatePicker.RangePicker
          allowClear
          placeholder={[t('voucher.vispReport.activatedFrom'), t('voucher.vispReport.activatedTo')]}
          value={filter.from && filter.to ? [dayjs(filter.from), dayjs(filter.to)] : null}
          onChange={(range) => {
            const picked = range?.[0] && range[1] ? rangeToIso([range[0], range[1]]) : null
            setFilter({ from: picked?.from ?? null, to: picked?.to ?? null })
          }}
        />
        {can('plan.read') ? (
          <Select<string | null>
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={t('voucher.activations.planPlaceholder')}
            value={filter.planId}
            onChange={(planId) => setFilter({ planId })}
            style={{ minWidth: 220 }}
            options={[...planNames].map(([id, label]) => ({ value: id, label }))}
          />
        ) : null}
        <InputNumber<number>
          min={1}
          precision={0}
          placeholder={t('voucher.activations.batchPlaceholder')}
          value={filter.batchNo ? Number(filter.batchNo) : null}
          onChange={(batchNo) => setFilter({ batchNo: batchNo ? String(batchNo) : null })}
          style={{ width: 160 }}
        />
      </Space>

      {report.isError ? (
        <QueryErrorState error={report.error} onRetry={() => void report.refetch()} />
      ) : (
        <Table<VoucherVispReportRow>
          dataSource={rows}
          columns={columns}
          rowKey={(row) => row.vispId ?? 'none'}
          loading={report.isPending}
          size="middle"
          pagination={false}
          scroll={{ x: 1000 }}
          locale={{
            emptyText: (
              <EmptyState title={t('voucher.vispReport.empty')} description={t('voucher.vispReport.emptyHint')} />
            ),
          }}
          summary={() =>
            rows.length > 1 ? (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Typography.Text strong>{t('voucher.vispReport.totalRow')}</Typography.Text>
                </Table.Summary.Cell>
                {COUNTS.map(({ key }, index) => (
                  <Table.Summary.Cell key={key} index={index + 1} align="right">
                    <Typography.Text strong>{formatNumber(sum(key))}</Typography.Text>
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={COUNTS.length + 1} align="right">
                  <Typography.Text strong>{formatNumber(sum('total'))}</Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null
          }
        />
      )}
    </>
  )
}
